import { createContext, useCallback, useContext, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { LoadingModal } from "./LoadingModal";

export interface LoadingModalContextValue {
  /** Wraps an async call with the loading modal: opens it, awaits fn, closes in finally.
   *  Re-throws any error so the caller can handle it normally. */
  withLoadingModal: <T>(fn: () => Promise<T>, message?: string) => Promise<T>;
  /** True while any withLoadingModal call is in flight. */
  loading: boolean;
}

interface LoadingModalProviderProps {
  children: ReactNode;
}

const LoadingModalContext = createContext<LoadingModalContextValue | null>(null);

/** Provides the loading modal to the subtree and renders it at the app root.
 *  Pair with useLoadingModal in any descendant — no JSX wiring needed at call sites. */
export function LoadingModalProvider({ children }: LoadingModalProviderProps) {
  const [state, setState] = useState<{ open: boolean; message?: string }>({ open: false });

  const withLoadingModal = useCallback(
    async <T,>(fn: () => Promise<T>, message?: string): Promise<T> => {
      setState({ open: true, message });
      try {
        return await fn();
      } finally {
        setState({ open: false });
      }
    },
    []
  );

  const value = useMemo(
    () => ({ withLoadingModal, loading: state.open }),
    [withLoadingModal, state.open]
  );

  return (
    <LoadingModalContext.Provider value={value}>
      {children}
      <LoadingModal open={state.open} message={state.message} />
    </LoadingModalContext.Provider>
  );
}

/** Access the loading modal from any descendant of LoadingModalProvider. */
export function useLoadingModal(): LoadingModalContextValue {
  const ctx = useContext(LoadingModalContext);
  if (!ctx) throw new Error("useLoadingModal must be used within <LoadingModalProvider>");
  return ctx;
}
